import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { LogOut, Download, Search, Filter } from "lucide-react";

const STATUSES = ["New", "Contacted", "Qualified", "Site Visit Scheduled", "Estimate Sent", "Follow-Up", "Won", "Lost"];
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [leads, setLeads] = useState([]);
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState({ q: "", status: "", priority: "" });
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = "Admin Dashboard · Final Cut Technologies"; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.q) params.q = filter.q;
            if (filter.status) params.status = filter.status;
            if (filter.priority) params.priority = filter.priority;
            const [{ data }, s] = await Promise.all([
                api.get("/admin/leads", { params }),
                api.get("/admin/stats"),
            ]);
            setLeads(data);
            setStats(s.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.priority]);

    const update = async (id, patch) => {
        try {
            const { data } = await api.patch(`/admin/leads/${id}`, patch);
            setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
            if (selected?.id === id) setSelected(data);
            toast.success("Updated");
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const exportCsv = async () => {
        const token = localStorage.getItem("fct_token");
        const res = await fetch(`${API}/admin/leads/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "finalcut-leads.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-bg text-ink">
            <Toaster position="top-right" />
            <header className="border-b border-line bg-bg">
                <div className="px-6 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <span className="w-9 h-9 grid place-items-center bg-ink overflow-hidden">
                            <img src="/brand/logo-fct.png" alt="Final Cut Technologies" className="w-full h-full object-contain" />
                        </span>
                        <div>
                            <p className="font-display text-sm tracking-tight leading-none">Final Cut Technologies</p>
                            <p className="tick text-muted mt-0.5">Lead Dashboard</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted hidden sm:block">{user?.email}</span>
                        <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm border border-line px-3 py-2 hover:bg-ink hover:text-bg transition-colors">
                            <LogOut className="w-4 h-4" /> Log out
                        </button>
                    </div>
                </div>
            </header>

            <main className="px-6 md:px-10 py-8 max-w-[1600px] mx-auto">
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        {[
                            ["Total leads", stats.total_leads],
                            ["New", stats.new_leads],
                            ["Last 7 days", stats.recent_7d],
                            ["Support requests", stats.support_requests],
                        ].map(([label, v]) => (
                            <div key={label} className="border border-line p-5 bg-bg">
                                <p className="tick text-muted">{label}</p>
                                <p className="font-display text-4xl mt-2">{v}</p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="border border-line bg-bg mb-6 p-4 flex flex-col md:flex-row gap-3 md:items-center">
                    <div className="flex-1 flex items-center gap-2 border border-line px-3">
                        <Search className="w-4 h-4 text-muted" />
                        <input value={filter.q} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && load()}
                            data-testid="admin-search" placeholder="Search name, company, email, city…"
                            className="w-full py-2 bg-transparent outline-none text-sm" />
                    </div>
                    <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} data-testid="admin-filter-status"
                        className="border border-line px-3 py-2 text-sm bg-bg">
                        <option value="">All statuses</option>
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <select value={filter.priority} onChange={(e) => setFilter((f) => ({ ...f, priority: e.target.value }))} data-testid="admin-filter-priority"
                        className="border border-line px-3 py-2 text-sm bg-bg">
                        <option value="">All priorities</option>
                        {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                    <button onClick={load} data-testid="admin-refresh" className="border border-line px-4 py-2 text-sm hover:bg-ink hover:text-bg transition-colors">Refresh</button>
                    <button onClick={exportCsv} data-testid="admin-export" className="inline-flex items-center gap-2 bg-ink text-bg px-4 py-2 text-sm hover:bg-accent transition-colors">
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-7 border border-line overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-bg-alt border-b border-line text-left">
                                <tr>
                                    <th className="p-3 tick text-muted">Name / Company</th>
                                    <th className="p-3 tick text-muted">Project</th>
                                    <th className="p-3 tick text-muted">Status</th>
                                    <th className="p-3 tick text-muted">Submitted</th>
                                </tr>
                            </thead>
                            <tbody data-testid="admin-leads-tbody">
                                {loading && <tr><td colSpan="4" className="p-8 text-center text-muted">Loading…</td></tr>}
                                {!loading && leads.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-muted">No leads yet.</td></tr>}
                                {leads.map((l) => (
                                    <tr key={l.id} onClick={() => setSelected(l)} data-testid={`admin-lead-row-${l.id}`}
                                        className={`border-b border-line cursor-pointer hover:bg-bg-alt ${selected?.id === l.id ? "bg-bg-alt" : ""}`}>
                                        <td className="p-3">
                                            <p className="font-medium">{l.name}</p>
                                            <p className="text-muted text-xs">{l.company || "—"}</p>
                                        </td>
                                        <td className="p-3">
                                            <p>{l.project_type || "—"}</p>
                                            <p className="text-muted text-xs">{l.city || "—"}</p>
                                        </td>
                                        <td className="p-3">
                                            <span className="inline-block px-2 py-1 text-xs border border-line">{l.status}</span>
                                        </td>
                                        <td className="p-3 text-xs text-muted">{new Date(l.submitted_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <aside className="lg:col-span-5">
                        {selected ? (
                            <div className="border border-line p-6 space-y-4 sticky top-6" data-testid="admin-lead-detail">
                                <p className="tick text-muted">Lead detail</p>
                                <h3 className="font-display text-2xl tracking-tight">{selected.name}</h3>
                                <p className="text-sm text-muted">{selected.company} · {selected.city}</p>
                                <div className="text-sm space-y-1 border-t border-line pt-4">
                                    <p><span className="text-muted">Email:</span> {selected.email}</p>
                                    <p><span className="text-muted">Phone:</span> {selected.phone || "—"}</p>
                                    <p><span className="text-muted">Business:</span> {selected.business_type || "—"}</p>
                                    <p><span className="text-muted">Locations:</span> {selected.number_of_locations || "—"}</p>
                                    <p><span className="text-muted">Project type:</span> {selected.project_type || "—"}</p>
                                    <p><span className="text-muted">Budget:</span> {selected.budget_range || "—"}</p>
                                    <p><span className="text-muted">Target:</span> {selected.target_completion_date || "—"}</p>
                                    <p><span className="text-muted">Services:</span> {(selected.services_needed || []).join(", ") || "—"}</p>
                                </div>
                                {selected.project_description && (
                                    <div className="border-t border-line pt-4">
                                        <p className="tick text-muted mb-1">Description</p>
                                        <p className="text-sm whitespace-pre-wrap">{selected.project_description}</p>
                                    </div>
                                )}

                                <div className="border-t border-line pt-4 grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="tick text-muted block mb-1">Status</label>
                                        <select value={selected.status} onChange={(e) => update(selected.id, { status: e.target.value })} data-testid="admin-detail-status"
                                            className="w-full border border-line px-2 py-2 text-sm">
                                            {STATUSES.map((s) => <option key={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="tick text-muted block mb-1">Priority</label>
                                        <select value={selected.priority || "Normal"} onChange={(e) => update(selected.id, { priority: e.target.value })} data-testid="admin-detail-priority"
                                            className="w-full border border-line px-2 py-2 text-sm">
                                            {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="tick text-muted block mb-1">Follow-up date</label>
                                        <input type="date" value={selected.followup_date || ""} onChange={(e) => update(selected.id, { followup_date: e.target.value })} data-testid="admin-detail-followup"
                                            className="w-full border border-line px-2 py-2 text-sm" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="tick text-muted block mb-1">Internal notes</label>
                                        <textarea rows="3" defaultValue={selected.internal_notes || ""} onBlur={(e) => update(selected.id, { internal_notes: e.target.value })} data-testid="admin-detail-notes"
                                            className="w-full border border-line px-2 py-2 text-sm" />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="border border-dashed border-line p-8 text-center text-muted text-sm">Select a lead to see details.</div>
                        )}
                    </aside>
                </div>
            </main>
        </div>
    );
}
